import { useMemo } from 'react';
import { useOrgCurrency } from '../../src/hooks/useOrgCurrency';
import { formatCurrency as fmtCur } from '../../src/utils/currency';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../src/hooks/useAuth';
import { useAppNav } from '../../src/contexts/app-nav.context';
import {
  Building2,
  DollarSign,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Loader2,
  Zap,
  ShoppingCart,
  Package,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { useProjects } from '../../src/hooks/queries/use-projects';
import { useBudgets } from '../../src/hooks/queries/use-budgets';
import { useChangeOrders } from '../../src/hooks/queries/use-change-orders';
import { usePurchaseOrders } from '../../src/hooks/queries/use-purchase-orders';
import { useInventoryItems } from '../../src/hooks/queries/use-inventory-items';
import { useTimesheets } from '../../src/hooks/queries/use-timesheets';
import { useBillingStatus } from '../../src/hooks/useBilling';
import { BillingCheckout } from '../BillingCheckout';
import { useState } from 'react';

export function Dashboard() {
  const { user, orgId } = useAuth();
  const orgCurrency = useOrgCurrency();
  const { navigate } = useAppNav();
  const { data: billingStatus } = useBillingStatus();
  const [showCheckout, setShowCheckout] = useState(false);

  const { data: projects = [], isLoading: projectsLoading } = useProjects(orgId);
  const { data: budgets = [], isLoading: budgetsLoading } = useBudgets(orgId);
  const { data: changeOrders = [] } = useChangeOrders(orgId);
  const { data: purchaseOrders = [] } = usePurchaseOrders(orgId);
  const { data: inventoryItems = [] } = useInventoryItems(orgId);
  const { data: timesheets = [] } = useTimesheets(orgId);

  const isActive = billingStatus?.status === 'active';
  const isTrial = billingStatus?.is_trial && !isActive;
  const isExpired = billingStatus?.status === 'expired';
  const daysLeft = billingStatus?.trial_days_remaining ?? 0;

  // Derived stats
  const stats = useMemo(() => {
    const activeProjects = projects.filter((p: any) => p.status === 'active');
    const totalBudget = budgets.reduce((sum: number, b: any) => sum + (Number(b.original_amount) || 0), 0);
    const totalActual = budgets.reduce((sum: number, b: any) => sum + (Number(b.actual_amount) || 0), 0);
    const totalCommitted = budgets.reduce((sum: number, b: any) => sum + (Number(b.committed_amount) || 0), 0);
    const budgetRemaining = totalBudget - totalActual - totalCommitted;
    const budgetUsedPct = totalBudget > 0 ? Math.round(((totalActual + totalCommitted) / totalBudget) * 100) : 0;

    const pendingTimesheets = timesheets.filter((t: any) => t.status === 'submitted').length;
    const pendingCOs = changeOrders.filter((co: any) => co.status === 'pending').length;
    const pendingPOs = purchaseOrders.filter((po: any) => po.status === 'pending_approval').length;
    const rawItems: any[] = Array.isArray(inventoryItems)
      ? inventoryItems
      : (inventoryItems as any)?.data ?? [];
    const lowStockItems = rawItems.filter(
      (i: any) => typeof i.current_stock === 'number' && i.current_stock <= (i.min_stock_level ?? 0)
    ).length;

    const latestProjects = [...projects]
      .sort((a: any, b: any) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime())
      .slice(0, 3);

    const totalAttentionItems = pendingTimesheets + pendingCOs + pendingPOs + lowStockItems;

    return {
      activeProjectCount: activeProjects.length,
      totalProjectCount: projects.length,
      totalBudget,
      totalActual,
      totalCommitted,
      budgetRemaining,
      budgetUsedPct,
      pendingTimesheets,
      pendingCOs,
      pendingPOs,
      lowStockItems,
      latestProjects,
      totalAttentionItems,
    };
  }, [projects, budgets, changeOrders, purchaseOrders, inventoryItems, timesheets]);

  const formatCurrency = (amount: number) => fmtCur(amount, orgCurrency);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  const loading = projectsLoading || budgetsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading dashboard...</span>
      </div>
    );
  }

  const budgetBarColor =
    stats.budgetUsedPct >= 90 ? 'bg-red-500' :
    stats.budgetUsedPct >= 75 ? 'bg-amber-500' :
    'bg-green-500';

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <Card className="dashboard-gradient-bg border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">
                Welcome back{user?.firstName ? `, ${user.firstName}` : ''}! ⚡
              </h2>
              <p className="text-muted-foreground mb-4">
                Your construction optimization dashboard. Monitor performance, track efficiency, and maximize project success.
              </p>
              {!isActive && (
                <Button onClick={() => setShowCheckout(true)} className="gap-2">
                  <Zap className="h-4 w-4" />
                  {isExpired ? 'Reactivate Subscription' : 'Upgrade to Pro'}
                </Button>
              )}
            </div>
            <div className="text-right">
              {isActive ? (
                <>
                  <div className="text-sm text-muted-foreground">Current plan</div>
                  <div className="text-2xl font-bold text-primary">
                    {billingStatus?.plan?.display_name ?? 'Active'}
                  </div>
                  <Badge className="mt-1">Active</Badge>
                </>
              ) : isExpired ? (
                <>
                  <div className="text-sm text-destructive font-medium">Trial expired</div>
                  <div className="text-2xl font-bold text-destructive">Upgrade now</div>
                </>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">Trial expires in</div>
                  <div className={`text-2xl font-bold ${daysLeft <= 3 ? 'text-destructive' : 'text-primary'}`}>
                    {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <BillingCheckout open={showCheckout} onClose={() => setShowCheckout(false)} />

      {/* Needs Attention Strip */}
      {stats.totalAttentionItems > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Needs Attention</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {stats.pendingTimesheets > 0 && (
                <button
                  onClick={() => navigate('/financial')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-100 hover:bg-amber-200 transition-colors text-sm text-amber-800"
                >
                  <Clock className="h-3.5 w-3.5" />
                  {stats.pendingTimesheets} timesheet{stats.pendingTimesheets !== 1 ? 's' : ''} pending approval
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
              {stats.pendingPOs > 0 && (
                <button
                  onClick={() => navigate('/procurement')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-100 hover:bg-amber-200 transition-colors text-sm text-amber-800"
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  {stats.pendingPOs} purchase order{stats.pendingPOs !== 1 ? 's' : ''} awaiting approval
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
              {stats.pendingCOs > 0 && (
                <button
                  onClick={() => navigate('/financial')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-100 hover:bg-amber-200 transition-colors text-sm text-amber-800"
                >
                  <FileText className="h-3.5 w-3.5" />
                  {stats.pendingCOs} change order{stats.pendingCOs !== 1 ? 's' : ''} pending
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
              {stats.lowStockItems > 0 && (
                <button
                  onClick={() => navigate('/procurement')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-100 hover:bg-red-200 transition-colors text-sm text-red-800"
                >
                  <Package className="h-3.5 w-3.5" />
                  {stats.lowStockItems} low stock item{stats.lowStockItems !== 1 ? 's' : ''}
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              Active Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">
              {stats.activeProjectCount}
            </div>
            <p className="text-xs text-blue-600 mt-1">
              {stats.totalProjectCount} total projects
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {formatCurrency(stats.totalBudget)}
            </div>
            <p className="text-xs text-green-600 mt-1">
              {formatCurrency(stats.budgetRemaining)} remaining
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-emerald-600" />
              Pending Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">
              {stats.pendingTimesheets + stats.pendingPOs + stats.pendingCOs}
            </div>
            <p className="text-xs text-emerald-600 mt-1">
              {stats.pendingTimesheets} timesheets · {stats.pendingPOs} POs · {stats.pendingCOs} COs
            </p>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br border ${stats.budgetUsedPct >= 90 ? 'from-red-50 to-red-100 border-red-200' : stats.budgetUsedPct >= 75 ? 'from-amber-50 to-amber-100 border-amber-200' : 'from-purple-50 to-purple-100 border-purple-200'}`}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${stats.budgetUsedPct >= 90 ? 'text-red-700' : stats.budgetUsedPct >= 75 ? 'text-amber-700' : 'text-purple-700'}`}>
              <BarChart3 className="h-4 w-4" />
              Budget Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.budgetUsedPct >= 90 ? 'text-red-700' : stats.budgetUsedPct >= 75 ? 'text-amber-700' : 'text-purple-700'}`}>
              {stats.budgetUsedPct}% used
            </div>
            <div className="mt-2 h-1.5 w-full bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${budgetBarColor}`} style={{ width: `${Math.min(stats.budgetUsedPct, 100)}%` }} />
            </div>
            <p className={`text-xs mt-1 ${stats.budgetUsedPct >= 90 ? 'text-red-600' : stats.budgetUsedPct >= 75 ? 'text-amber-600' : 'text-purple-600'}`}>
              {formatCurrency(stats.totalActual)} spent · {formatCurrency(stats.totalCommitted)} committed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* System Status & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
            <CardDescription>Current operational overview</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              {stats.activeProjectCount > 0 ? (
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {stats.activeProjectCount} Active Project{stats.activeProjectCount !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stats.totalProjectCount} total across all statuses
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/projects')}>
                View
              </Button>
            </div>

            <div className="flex items-start gap-3">
              {stats.pendingTimesheets === 0 ? (
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              ) : (
                <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {stats.pendingTimesheets === 0
                    ? 'No timesheets pending'
                    : `${stats.pendingTimesheets} Timesheet${stats.pendingTimesheets !== 1 ? 's' : ''} Pending`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stats.pendingTimesheets === 0 ? 'All timesheets up to date' : 'Require approval'}
                </p>
              </div>
              {stats.pendingTimesheets > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/financial')}>
                  Review
                </Button>
              )}
            </div>

            <div className="flex items-start gap-3">
              {stats.pendingPOs === 0 ? (
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              ) : (
                <ShoppingCart className="h-5 w-5 text-blue-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {stats.pendingPOs === 0
                    ? 'No POs awaiting approval'
                    : `${stats.pendingPOs} Purchase Order${stats.pendingPOs !== 1 ? 's' : ''} Pending`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stats.pendingPOs === 0 ? 'Procurement up to date' : 'Awaiting approval'}
                </p>
              </div>
              {stats.pendingPOs > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/procurement')}>
                  Review
                </Button>
              )}
            </div>

            <div className="flex items-start gap-3">
              {stats.lowStockItems === 0 ? (
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              ) : (
                <Package className="h-5 w-5 text-red-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {stats.lowStockItems === 0
                    ? 'All inventory well stocked'
                    : `${stats.lowStockItems} Low Stock Item${stats.lowStockItems !== 1 ? 's' : ''}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stats.lowStockItems === 0 ? 'Inventory levels normal' : 'Below minimum threshold'}
                </p>
              </div>
              {stats.lowStockItems > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/procurement')}>
                  View
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="h-auto p-4 flex flex-col items-start gap-2" onClick={() => navigate('/projects')}>
              <Building2 className="h-5 w-5 text-primary" />
              <div className="text-left">
                <p className="font-medium">New Project</p>
                <p className="text-xs text-muted-foreground">Start a new construction project</p>
              </div>
            </Button>
            <Button variant="outline" className="h-auto p-4 flex flex-col items-start gap-2" onClick={() => navigate('/reports')}>
              <BarChart3 className="h-5 w-5 text-green-600" />
              <div className="text-left">
                <p className="font-medium">View Reports</p>
                <p className="text-xs text-muted-foreground">Analytics and insights</p>
              </div>
            </Button>
            <Button variant="outline" className="h-auto p-4 flex flex-col items-start gap-2" onClick={() => navigate('/financial')}>
              <DollarSign className="h-5 w-5 text-blue-600" />
              <div className="text-left">
                <p className="font-medium">Budget Review</p>
                <p className="text-xs text-muted-foreground">Check financial status</p>
              </div>
            </Button>
            <Button variant="outline" className="h-auto p-4 flex flex-col items-start gap-2" onClick={() => navigate('/schedules')}>
              <Users className="h-5 w-5 text-purple-600" />
              <div className="text-left">
                <p className="font-medium">Team Tasks</p>
                <p className="text-xs text-muted-foreground">Assign and track work</p>
              </div>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Latest Projects */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Latest Projects</CardTitle>
              <CardDescription>Your 3 most recent projects</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/projects')}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.latestProjects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No projects found</p>
                <Button className="mt-4" size="sm" onClick={() => navigate('/projects')}>
                  Create New Project
                </Button>
              </div>
            ) : (
              stats.latestProjects.map((project: any) => {
                const statusVariant: Record<string, string> = {
                  active: 'default',
                  on_hold: 'secondary',
                  completed: 'default',
                  cancelled: 'destructive',
                  planning: 'secondary',
                };
                const statusColor: Record<string, string> = {
                  active: 'bg-green-100 text-green-800',
                  on_hold: 'bg-amber-100 text-amber-800',
                  completed: 'bg-blue-100 text-blue-800',
                  cancelled: 'bg-red-100 text-red-800',
                  planning: 'bg-purple-100 text-purple-800',
                };
                const pct = project.completion_percentage ?? 0;
                const pctColor = pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-blue-500';

                return (
                  <div key={project.project_id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{project.project_name}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        {project.total_budget ? (
                          <span>Budget: {formatCurrency(project.total_budget)}</span>
                        ) : null}
                        {project.end_date ? (
                          <span>Due: {formatDate(project.end_date)}</span>
                        ) : null}
                      </div>
                      {pct > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${pctColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground w-8 text-right">{pct}%</span>
                        </div>
                      )}
                    </div>
                    <div className="ml-4 shrink-0">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColor[project.status] ?? 'bg-muted text-muted-foreground'}`}>
                        {project.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
